import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { loginThunk, clearError } from '../../../shared/store/authSlice';
import type { UserRole } from '../../../shared/types/UserRole';
import logo from '../../../assets/img/logo/artecraftindia.jpg';
import './login.css';

const loginSchema = z.object({
    email:    z.string().email('Enter a valid email'),
    password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

const ROLE_ROUTES: Record<UserRole, string> = {
    USER:        '/user/dashboard',
    SELLER:      '/seller/dashboard',
    AGENT:       '/agent/dashboard',
    ADMIN:       '/admin/dashboard',
    SUPER_ADMIN: '/super-admin/dashboard',
};

const EyeOpen: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const EyeClosed: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

export const LoginPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { loading, error, user, isAuthenticated } = useAppSelector((s) => s.auth);
    const [showPassword, setShowPassword] = useState(false);

    const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>({
        resolver: zodResolver(loginSchema),
    });

    useEffect(() => { dispatch(clearError()); }, [dispatch]);

    useEffect(() => {
        if (isAuthenticated && user) {
            const redirect = searchParams.get('redirect');
            const isSafeRedirect = redirect && redirect.startsWith('/') && !redirect.startsWith('//');
            if (isSafeRedirect && user.role === 'USER') {
                navigate(redirect, { replace: true });
            } else {
                navigate(ROLE_ROUTES[user.role] ?? '/');
            }
        }
    }, [isAuthenticated, user, navigate, searchParams]);

    const onSubmit = (data: LoginForm) => { dispatch(loginThunk(data)); };

    return (
        <>
            <div className="login-page">

                {/* ── Brand panel ── */}
                <div className="login-brand">
                    <div className="login-brand-content">

                        <div className="login-brand-logo">
                            <img src={logo} alt="Arte Craft India" />
                        </div>

                        <div className="login-brand-text">
                            <h2>Where Tradition<br />Meets Craft</h2>
                            <p>Authentic Indian handicrafts, handpicked from verified artisans across the country.</p>
                        </div>

                        <ul className="login-trust-list">
                            <li>
                                <span className="trust-dot" />
                                Verified Sellers &amp; Artisans
                            </li>
                            <li>
                                <span className="trust-dot" />
                                10,000+ Handcrafted Products
                            </li>
                            <li>
                                <span className="trust-dot" />
                                Secure &amp; Trusted Payments
                            </li>
                        </ul>
                    </div>

                    {/* decorative radial rings */}
                    <div className="login-brand-rings" aria-hidden="true">
                        <span /><span /><span />
                    </div>
                </div>

                {/* ── Form panel ── */}
                <div className="login-form-panel">
                    <div className="login-card">

                        {/* logo visible only on mobile */}
                        <div className="login-mobile-logo">
                            <img src={logo} alt="Arte Craft India" />
                        </div>

                        <div className="login-header">
                            <h1>Welcome Back</h1>
                            <p>Sign in to your account to continue</p>
                        </div>

                        {error && (
                            <div className="login-error" role="alert">
                                <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15" aria-hidden="true">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>

                            <div className={`form-group${errors.email ? ' has-error' : ''}`}>
                                <label htmlFor="email">Email Address</label>
                                <input
                                    id="email"
                                    type="email"
                                    {...register('email')}
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                />
                                {errors.email && <span className="field-error">{errors.email.message}</span>}
                            </div>

                            <div className={`form-group${errors.password ? ' has-error' : ''}`}>
                                <label htmlFor="password">Password</label>
                                <div className="password-wrapper">
                                    <input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        {...register('password')}
                                        placeholder="••••••••"
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(v => !v)}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <EyeClosed /> : <EyeOpen />}
                                    </button>
                                </div>
                                {errors.password && <span className="field-error">{errors.password.message}</span>}
                            </div>

                            <div className="login-forgot">
                                <Link to="/forgot-password">Forgot password?</Link>
                            </div>

                            <button type="submit" className="login-btn" disabled={loading}>
                                {loading
                                    ? <><span className="login-spinner" aria-hidden="true" /> Signing in…</>
                                    : 'Sign In'
                                }
                            </button>
                        </form>

                        <div className="login-divider"><span>New to Arte Craft India?</span></div>

                        <div className="login-footer">
                            <Link to="/register" className="login-register-btn">Create an Account</Link>
                            <p className="login-seller-link">
                                Are you a seller? <Link to="/seller/register">Register here</Link>
                            </p>
                        </div>

                    </div>
                </div>

            </div>
        </>
    );
};
